import { DayDate } from "@cmp-src/data-display/dates/DayDate";
import { AuthenticatedUser, TacticApiClient } from "@gettactic/api";
import { OfficeStatusCell } from "@gettactic/components/src/components/WeeklyOffice/cells/OfficeStatusCell";
import { UserComponent } from "@gettactic/components/src/components/WeeklyOffice/cells/UserCell";
import { getWorkingLocationStatus } from "@gettactic/components/src/components/WeeklyOffice/cells/WorkingLocationPresenceIcon";
import { makeWeeklyTableId } from "@gettactic/components/src/components/WeeklyOffice/weekly-office-helpers";
import {
  cn,
  getFirstMondayBefore,
  limitWeekDateStrings,
  utcToWallClock
} from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import {
  useAuthenticated,
  useMyAgenda,
  useWeekStartsOn
} from "@gettactic/hooks";
import React from "react";
import { useDashboardFilters } from "@/features/dashboard/DashboardStateContext";
import { WeeklyTableSkeleton } from "./WeeklyTableSkeleton";

interface AuthenticatedUserRowProps {
  authenticatedUser: AuthenticatedUser;
  headers: string[];
  favsApi: TacticApiClient;
  columnCount: number;
  showAsMobileDaily?: boolean;
  showMobileCells?: boolean;
  selectedDayIndex?: number;
  setSelectedDayIndex?: (index: number) => void;
}

export const AuthenticatedUserRow = ({
  authenticatedUser,
  headers,
  favsApi,
  columnCount,
  showAsMobileDaily,
  showMobileCells,
  selectedDayIndex,
  setSelectedDayIndex
}: AuthenticatedUserRowProps) => {
  const { filters } = useDashboardFilters();
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const {
    userContext: { deviceTz, currentOffice }
  } = useAuthenticated();

  const weekStartsOn = useWeekStartsOn();
  const firstMondayThisWeek = getFirstMondayBefore(
    filters.weekDate,
    weekStartsOn
  );
  const { data: myRow } = useMyAgenda({
    from: firstMondayThisWeek,
    user_tz: deviceTz
  });

  const currentOfficeId = authenticatedUser?.officeId;
  const userId = authenticatedUser?.user?.id;

  if (!myRow || !Object.keys(myRow?.agenda ?? {}).length) {
    return (
      <WeeklyTableSkeleton
        myUser={authenticatedUser?.user}
        columnCount={columnCount}
      />
    );
  }

  const days = myRow?.days ? Object.keys(myRow.days) : [];

  if (!days.length) {
    return (
      <WeeklyTableSkeleton
        myUser={authenticatedUser?.user}
        columnCount={columnCount}
      />
    );
  }

  const dayCellsCount = columnCount - 1;
  const limitedDays = limitWeekDateStrings(days, dayCellsCount);

  const cells = (
    <>
      {/* Day cells */}
      {limitedDays.map((dateStr: string, index: number) => {
        const dayData = myRow?.agenda?.[dateStr];
        if (!showMobileCells && !isMd && index !== selectedDayIndex)
          return null;
        let status = "unknown";
        if (dayData) {
          status = getWorkingLocationStatus({
            assignedPolicyType: dayData?.policy?.type ?? null,
            filterOfficeId:
              filters.offices.length > 0
                ? filters.offices
                : (currentOfficeId ?? null),
            officeIdOrOtherLocation: currentOfficeId || "",
            locationIsAssigned: !!dayData,
            assignedLocation: dayData?.location,
            assignedOfficeId: dayData?.office_id,
            assignedResource: dayData?.schedules[0]?.resource
          });
        }

        // The agenda payload is true UTC; the /map deep link params are read as
        // office wall-clock time, so convert before handing them to the cell.
        const dayOfficeTz =
          (dayData?.office_id
            ? authenticatedUser.offices?.byId[dayData.office_id]?.time_zone
            : null) ??
          currentOffice?.time_zone ??
          deviceTz;
        const schedule = dayData?.schedules[0];
        const startTime = schedule?.start
          ? utcToWallClock(schedule.start, dayOfficeTz)
          : undefined;
        const endTime = schedule?.end
          ? utcToWallClock(schedule.end, dayOfficeTz)
          : undefined;

        const childId = makeWeeklyTableId(headers, dateStr, index);

        if (showMobileCells) {
          return (
            <button
              className={cn(
                "w-full border-b-2 px-1 pt-[18px] pb-2",
                selectedDayIndex === index
                  ? "border-b-[#656CFF]"
                  : "border-b-transparent"
              )}
              key={childId}
              onClick={() => setSelectedDayIndex?.(index)}
            >
              <DayDate
                apiDate={dateStr}
                className={"mb-2 space-x-1 text-[14px]"}
                dayClassNameNAme={"font-normal text-neutral-1000"}
                dateClassNAme={"font-normal text-neutral-1000"}
              />
              <div className={"pointer-events-none"}>
                <OfficeStatusCell
                  officeName={
                    dayData?.office_id
                      ? authenticatedUser.offices?.byId[dayData.office_id]?.name
                      : ""
                  }
                  officeId={dayData?.office_id || ""}
                  status={status}
                  // TBD: use target slot here
                  resource={dayData?.schedules[0]?.resource}
                  date={dateStr}
                  startTime={startTime}
                  endTime={endTime}
                  onClick={(e) => e.preventDefault()}
                  isFlat
                />
              </div>
            </button>
          );
        }

        return (
          <div
            key={childId}
            className="m-auto flex h-full w-full min-w-10 items-center justify-center md:min-w-auto"
          >
            <div className="w-full">
              <OfficeStatusCell
                officeName={
                  dayData?.office_id
                    ? authenticatedUser.offices?.byId[dayData.office_id]?.name
                    : ""
                }
                officeId={dayData?.office_id || ""}
                status={status}
                // TBD: use target slot here
                resource={dayData?.schedules[0]?.resource}
                date={dateStr}
                startTime={startTime}
                endTime={endTime}
              />
            </div>
          </div>
        );
      })}
    </>
  );

  if (showMobileCells) {
    return cells;
  }

  return (
    <React.Fragment key={`auth-user-${userId}`}>
      {/* Name cell */}
      <div
        key="auth-name-cell"
        className="m-auto flex h-full w-full items-center border-b border-b-neutral-100 md:border-b-0"
      >
        <div className="h-full w-full">
          <UserComponent
            hasAnyFav={false}
            user={authenticatedUser.user}
            favsApi={favsApi}
            isMe={true}
          />
        </div>
        {!isMd && <div className={"flex gap-1"}>{cells}</div>}
      </div>
      {!showAsMobileDaily &&
        (isMd ? cells : <div className={"flex gap-1"}>{cells}</div>)}{" "}
    </React.Fragment>
  );
};
